import { z } from 'zod';

/**
 * Moteur juridique (pack juridique Prestige Habitat, 25/09/2026) :
 *  - UN socle de CGV pour particuliers et professionnels, mentions conditionnelles ;
 *  - coordonnées légales de l'entreprise administrables, injectées dans les textes ({{variables}}) ;
 *  - règle de rétractation versionnée (C. consom. L221-18, L221-3, L221-25) ;
 *  - consentements affichés selon des conditions (C5 exécution anticipée, C6 réserves, C7 marketing facultatif) ;
 *  - contrôle « CONFIGURATION JURIDIQUE INCOMPLÈTE » avant activation.
 */

/* ------------------------------------------------------------------ coordonnées légales (paramètres entreprise) */

export interface CompanyLegalField {
  key: string;
  label: string;
  group: 'identite' | 'contacts' | 'mediation' | 'assurance' | 'conditions';
  /** exigé pour activer la signature (configuration juridique complète) */
  required?: boolean;
  multiline?: boolean;
}

export const COMPANY_LEGAL_FIELDS: CompanyLegalField[] = [
  { key: 'legalName', label: 'Raison sociale', group: 'identite', required: true },
  { key: 'legalForm', label: 'Forme juridique', group: 'identite', required: true },
  { key: 'siren', label: 'SIREN', group: 'identite', required: true },
  { key: 'siret', label: 'SIRET', group: 'identite', required: true },
  { key: 'rcs', label: 'RCS', group: 'identite' },
  { key: 'vatNumber', label: 'N° de TVA intracommunautaire', group: 'identite' },
  { key: 'address', label: 'Siège social', group: 'identite', required: true },
  { key: 'activity', label: 'Activité', group: 'identite', multiline: true },
  { key: 'contactEmail', label: 'E-mail de contact', group: 'contacts', required: true },
  { key: 'contactPhone', label: 'Téléphone', group: 'contacts' },
  { key: 'complaintsEmail', label: 'E-mail réclamations', group: 'contacts', required: true },
  { key: 'complaintsAddress', label: 'Adresse postale / téléphone réclamations', group: 'contacts' },
  { key: 'rgpdEmail', label: 'E-mail données personnelles (RGPD)', group: 'contacts', required: true },
  { key: 'rgpdAddress', label: 'Adresse RGPD', group: 'contacts' },
  { key: 'mediatorName', label: 'Médiateur de la consommation', group: 'mediation', required: true },
  { key: 'mediatorAddress', label: 'Adresse du médiateur', group: 'mediation', required: true },
  { key: 'mediatorWebsite', label: 'Site internet du médiateur', group: 'mediation', required: true },
  { key: 'insurerName', label: 'Assureur', group: 'assurance' },
  { key: 'insuranceCoverage', label: 'Couverture géographique de l’assurance', group: 'assurance' },
  {
    key: 'commercialWarranty',
    label: 'Garantie commerciale (durée, pièces, main-d’œuvre, exclusions)',
    group: 'conditions',
    multiline: true,
  },
  {
    key: 'b2bPaymentTerms',
    label: 'Délais et pénalités de paiement (professionnels)',
    group: 'conditions',
    multiline: true,
  },
  { key: 'marketingChannels', label: 'Canaux des communications commerciales', group: 'conditions' },
  { key: 'withdrawalDays', label: 'Délai de rétractation (jours)', group: 'conditions' },
  {
    key: 'proMaxEmployees',
    label: 'Professionnels protégés : salariés au plus (L221-3)',
    group: 'conditions',
  },
];
export const COMPANY_LEGAL_GROUPS: Record<CompanyLegalField['group'], string> = {
  identite: 'Identité',
  contacts: 'Contacts (réclamations, RGPD)',
  mediation: 'Médiation de la consommation',
  assurance: 'Assurance',
  conditions: 'Conditions',
};

export type CompanyLegal = Partial<Record<string, string>>;

/* ------------------------------------------------------------------ émetteur des devis : l'entreprise ou un partenaire */

/**
 * Pendant une période, l'entreprise peut facturer sur la société d'un partenaire : seules ces coordonnées
 * (identité de l'émetteur) changent sur les devis / bons de commande, et le logo n'y apparaît pas. Tout le reste
 * (textes, médiateur, garanties, application) est inchangé.
 */
export const ISSUER_FIELDS = [
  'legalName',
  'legalForm',
  'siren',
  'siret',
  'rcs',
  'vatNumber',
  'address',
  'contactEmail',
  'contactPhone',
] as const;
export type IssuerField = (typeof ISSUER_FIELDS)[number];
export interface PartnerIssuer {
  active: boolean;
  identity: Partial<Record<IssuerField, string>>;
}
export const partnerIssuerInput = z.object({
  active: z.boolean(),
  identity: companyLegalSchemaFor(ISSUER_FIELDS),
});

/** Coordonnées utilisées sur les devis : celles de l'entreprise, ou celles du partenaire quand le mode est actif. */
export function effectiveCompany(
  legal: CompanyLegal | null | undefined,
  partner: PartnerIssuer | null | undefined,
): CompanyLegal {
  const own = legal ?? {};
  if (!partner?.active) return own;
  const out: CompanyLegal = { ...own, issuedFor: 'partner' };
  for (const k of ISSUER_FIELDS) out[k] = partner.identity[k]?.trim() || '';
  return out;
}
export const isPartnerIssued = (company: CompanyLegal | null | undefined) => company?.issuedFor === 'partner';

function companyLegalSchemaFor(keys: readonly string[]) {
  return z
    .object(
      Object.fromEntries(
        COMPANY_LEGAL_FIELDS.filter((f) => keys.includes(f.key)).map((f) => {
          let s = z.string().trim().max(300);
          if (f.key === 'siren') s = s.regex(/^(\d{9})?$/, 'SIREN : 9 chiffres');
          if (f.key === 'siret') s = s.regex(/^(\d{14})?$/, 'SIRET : 14 chiffres');
          if (f.key.endsWith('Email')) s = s.regex(/^([^\s@]+@[^\s@]+\.[^\s@]+)?$/, 'E-mail invalide');
          return [f.key, s.optional()];
        }),
      ),
    )
    .partial();
}

export const companyLegalSchema = z
  .object(
    Object.fromEntries(
      COMPANY_LEGAL_FIELDS.map((f) => {
        let s = z
          .string()
          .trim()
          .max(f.multiline ? 4000 : 300);
        if (f.key === 'siren') s = s.regex(/^(\d{9})?$/, 'SIREN : 9 chiffres');
        if (f.key === 'siret') s = s.regex(/^(\d{14})?$/, 'SIRET : 14 chiffres');
        if (f.key.endsWith('Email')) s = s.regex(/^([^\s@]+@[^\s@]+\.[^\s@]+)?$/, 'E-mail invalide');
        if (f.key === 'withdrawalDays' || f.key === 'proMaxEmployees')
          s = s.regex(/^(\d{1,3})?$/, 'Nombre entier');
        return [f.key, s.optional()];
      }),
    ),
  )
  .partial();

/* ------------------------------------------------------------------ conditions de vente et rétractation */

export const SALE_CHANNELS = ['off_premises', 'distance', 'premises'] as const;
export type SaleChannel = (typeof SALE_CHANNELS)[number];
export const SALE_CHANNEL_LABELS: Record<SaleChannel, string> = {
  off_premises: 'Hors établissement (chez le client)',
  distance: 'À distance',
  premises: 'En agence / établissement',
};
export type CustomerType = 'consumer' | 'professional';
export const CUSTOMER_TYPE_LABELS: Record<CustomerType, string> = {
  consumer: 'Particulier / consommateur',
  professional: 'Professionnel',
};
export const customerTypeOf = (clientKind: string): CustomerType =>
  clientKind === 'company' ? 'professional' : 'consumer';

export const WITHDRAWAL_RULE = 'fr-conso-l221-v1';

export interface SaleContext {
  customerType: CustomerType;
  saleChannel: SaleChannel;
  proEmployeeCount: number | null;
  proOutsideMainActivity: boolean | null;
}

/**
 * Droit de rétractation (règle versionnée, paramètres administrables) :
 *  - consommateur, contrat hors établissement ou à distance : oui (L221-18) ;
 *  - professionnel, hors établissement, objet hors de son activité principale, au plus N salariés : oui (L221-3) ;
 *  - sinon : non.
 */
export function withdrawalRight(ctx: SaleContext, company: CompanyLegal | null | undefined) {
  const days = Number(company?.withdrawalDays || 14);
  const maxEmployees = Number(company?.proMaxEmployees || 5);
  let applies = false;
  let basis: string | null = null;
  if (
    ctx.customerType === 'consumer' &&
    (ctx.saleChannel === 'off_premises' || ctx.saleChannel === 'distance')
  ) {
    applies = true;
    basis = 'L221-18';
  } else if (
    ctx.customerType === 'professional' &&
    ctx.saleChannel === 'off_premises' &&
    ctx.proOutsideMainActivity === true &&
    ctx.proEmployeeCount !== null &&
    ctx.proEmployeeCount <= maxEmployees
  ) {
    applies = true;
    basis = 'L221-3';
  }
  return { applies, basis, days, rule: WITHDRAWAL_RULE };
}

/* ------------------------------------------------------------------ textes et conditions d'affichage */

export const CONSENT_CONDITIONS = [
  'always',
  'if_reserves',
  'if_early_start',
  'if_withdrawal',
  'consumer_only',
  'professional_only',
] as const;
export type ConsentCondition = (typeof CONSENT_CONDITIONS)[number];
export const CONSENT_CONDITION_LABELS: Record<ConsentCondition, string> = {
  always: 'Toujours',
  if_reserves: 'Si au moins une réserve',
  if_early_start: 'Si rétractation applicable ET exécution anticipée demandée',
  if_withdrawal: 'Si un droit de rétractation s’applique',
  consumer_only: 'Particuliers uniquement',
  professional_only: 'Professionnels uniquement',
};

export function conditionMet(
  condition: string | null | undefined,
  ctx: { hasReserves: boolean; withdrawal: boolean; earlyStart: boolean; customerType: CustomerType },
) {
  switch ((condition ?? 'always') as ConsentCondition) {
    case 'if_reserves':
      return ctx.hasReserves;
    case 'if_early_start':
      return ctx.withdrawal && ctx.earlyStart;
    case 'if_withdrawal':
      return ctx.withdrawal;
    case 'consumer_only':
      return ctx.customerType === 'consumer';
    case 'professional_only':
      return ctx.customerType === 'professional';
    default:
      return true;
  }
}

/* ------------------------------------------------------------------ variables des textes */

/** Variables utilisables dans les textes : {{nom}}. Absente ou vide → « [à compléter : nom] ». */
export const LEGAL_VARIABLES: Record<string, string> = {
  'entreprise.raison_sociale': 'legalName',
  'entreprise.forme': 'legalForm',
  'entreprise.siren': 'siren',
  'entreprise.siret': 'siret',
  'entreprise.rcs': 'rcs',
  'entreprise.tva': 'vatNumber',
  'entreprise.adresse': 'address',
  'entreprise.email': 'contactEmail',
  'entreprise.telephone': 'contactPhone',
  'reclamations.email': 'complaintsEmail',
  'reclamations.adresse': 'complaintsAddress',
  'rgpd.email': 'rgpdEmail',
  'rgpd.adresse': 'rgpdAddress',
  'mediateur.nom': 'mediatorName',
  'mediateur.adresse': 'mediatorAddress',
  'mediateur.site': 'mediatorWebsite',
  'assureur.nom': 'insurerName',
  'assureur.couverture': 'insuranceCoverage',
  'garantie.commerciale': 'commercialWarranty',
  'paiement.professionnels': 'b2bPaymentTerms',
  'communications.canaux': 'marketingChannels',
  'retractation.delai': 'withdrawalDays',
};
const VAR_RE = /\{\{\s*([a-z_.]+)\s*\}\}/g;
export const MISSING_PREFIX = '[à compléter : ';

export function legalVars(
  company: CompanyLegal | null | undefined,
  extra: Record<string, string | null | undefined> = {},
) {
  const vars: Record<string, string | null | undefined> = {};
  for (const [name, key] of Object.entries(LEGAL_VARIABLES)) vars[name] = company?.[key];
  if (!vars['retractation.delai']) vars['retractation.delai'] = '14';
  return { ...vars, ...extra };
}

/** coordonnées facultatives : vides, elles sont simplement omises du texte */
const OPTIONAL_VARS = new Set(
  Object.entries(LEGAL_VARIABLES)
    .filter(([, key]) => !COMPANY_LEGAL_FIELDS.find((f) => f.key === key)?.required)
    .map(([name]) => name),
);

export function renderLegalText(body: string, vars: Record<string, string | null | undefined>): string {
  return body
    .replace(/\r\n/g, '\n')
    .trim()
    .replace(VAR_RE, (_m, name: string) => {
      const v = vars[name];
      if (v && String(v).trim()) return String(v).trim();
      return OPTIONAL_VARS.has(name) ? '\u0000' : `${MISSING_PREFIX}${name}]`;
    })
    .replace(/ ?[-–,] ?\u0000|\u0000 ?[-–,] ?|\u0000/g, '')
    .replace(/ +([.,;])/g, '$1');
}

/** Mentions restant à compléter : [CROCHETS EN MAJUSCULES] hérités d'un modèle, ou variables vides. */
export function unresolvedPlaceholders(rendered: string): string[] {
  const out = new Set<string>();
  for (const m of rendered.matchAll(/\[(?:à compléter : [a-z_.]+|[A-ZÀ-Ý0-9][A-ZÀ-Ý0-9 ’'./-]{2,})\]/g))
    out.add(m[0]);
  return [...out];
}

/* ------------------------------------------------------------------ configuration juridique */

export interface LegalTextLike {
  kind: string;
  code: string;
  title: string;
  body: string;
  isActive?: boolean;
  reviewed?: boolean;
}

/** « CONFIGURATION JURIDIQUE INCOMPLÈTE » : liste des manques (vide = prêt). */
export function legalConfigIssues(
  company: CompanyLegal | null | undefined,
  texts: readonly LegalTextLike[],
): string[] {
  const issues: string[] = [];
  for (const f of COMPANY_LEGAL_FIELDS)
    if (f.required && !company?.[f.key]?.trim())
      issues.push(`${COMPANY_LEGAL_GROUPS[f.group]} : ${f.label} manquant`);
  const active = texts.filter((t) => t.isActive !== false);
  if (!active.some((t) => t.kind === 'terms')) issues.push('Aucune condition générale de vente active');
  if (!active.some((t) => t.kind === 'privacy'))
    issues.push('Aucune politique de confidentialité / mention RGPD active');
  if (!active.some((t) => t.kind === 'withdrawal')) issues.push('Formulaire de rétractation non configuré');
  if (company?.commercialWarranty === undefined && active.some((t) => /garantie\.commerciale/.test(t.body))) {
    issues.push('Garantie commerciale annoncée mais non décrite');
  }
  const vars = legalVars(company);
  for (const t of active) {
    const missing = unresolvedPlaceholders(renderLegalText(t.body, vars)).filter(
      (p) => !/commentaire|^\[à compléter : (dossier|reserve|client|commande)\./i.test(p),
    );
    if (missing.length && t.kind !== 'reserve' && t.kind !== 'withdrawal')
      issues.push(`« ${t.title} » : ${missing.join(', ')}`);
  }
  return issues;
}

/* ------------------------------------------------------------------ tampon et signature du gérant */

export const DOCUMENT_IMAGE_SLOTS = ['stamp', 'signature', 'partnerStamp', 'partnerSignature', 'logo'] as const;
export type DocumentImageSlot = (typeof DOCUMENT_IMAGE_SLOTS)[number];
export const DOCUMENT_IMAGE_LABELS: Record<DocumentImageSlot, string> = {
  stamp: 'Tampon de l’entreprise',
  signature: 'Signature du gérant',
  partnerStamp: 'Tampon du partenaire',
  partnerSignature: 'Signature du partenaire',
  logo: 'Logo de l’entreprise',
};
/** Adresse publique du logo importé d'une entreprise (le numéro de version évite l'ancien logo en cache). */
export const tenantLogoUrl = (tenantId: string, sha256: string) => `/v1/public/logo/${tenantId}?v=${sha256.slice(0, 12)}`;

/** Réglages des documents émis (paramètres de l'entreprise, `settings.documents`). */
export interface DocumentSettings {
  /** tampon + signature du gérant sur les devis / bons de commande */
  stampOnQuotes: boolean;
  /** tampon + signature du gérant sur les factures (utilisé dès que les factures sont produites par Krotime) */
  stampOnInvoices: boolean;
  /** images présentes (empreinte SHA-256 de la version en place) */
  images: Partial<Record<DocumentImageSlot, string>>;
  partner: PartnerIssuer;
}
export const documentSettingsInput = z.object({
  stampOnQuotes: z.boolean(),
  stampOnInvoices: z.boolean(),
  partner: partnerIssuerInput,
});

export function documentSettingsOf(settings: unknown): DocumentSettings {
  const d = ((settings ?? {}) as { documents?: Partial<DocumentSettings> }).documents ?? {};
  return {
    stampOnQuotes: d.stampOnQuotes ?? false,
    stampOnInvoices: d.stampOnInvoices ?? false,
    images: d.images ?? {},
    partner: { active: d.partner?.active ?? false, identity: d.partner?.identity ?? {} },
  };
}
